import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useStudentAuth } from '../hooks/useStudentAuth';
import SEO from '../components/SEO';
import { Eye, EyeOff, CheckCircle2, Circle, Loader2, ArrowLeft, Lock } from 'lucide-react';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword } = useStudentAuth();

  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase auto-handles the recovery link via onAuthStateChange (PASSWORD_RECOVERY event)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setHasRecoverySession(true);
      }
    });

    // Also check if we already have a session from the link
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasRecoverySession(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  };
  const passwordScore = Object.values(passwordChecks).filter(Boolean).length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (passwordScore < 3) {
      setError('Bitte ein stärkeres Passwort wählen (mind. 3 Kriterien).');
      return;
    }
    if (password !== confirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    setSubmitting(true);
    try {
      await updatePassword(password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Fehler';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none bg-white text-[#111827] placeholder-[#9b9490]';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <SEO title="Passwort zurücksetzen – Arabicroots" url="/reset-password" noIndex />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#6b6b6b] hover:text-[#970000] text-sm mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Zur Anmeldung
          </Link>
          <div className="flex items-center justify-center gap-3 mb-2">
            <img
              src="/LogoHeader4.png"
              alt="Arabicroots"
              className="w-10 h-10 object-contain"
              onError={e => (e.currentTarget.style.display = 'none')}
            />
            <span className="text-2xl font-bold text-[#111827]">Arabicroots</span>
          </div>
        </div>

        <div className="bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.07)] p-8">
          {hasRecoverySession === null ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-7 w-7 text-[#970000] animate-spin" />
              <p className="text-sm text-[#6b6b6b]">Link wird geprüft…</p>
            </div>
          ) : done ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-[#111827] mb-2">Passwort geändert</h3>
              <p className="text-[#6b6b6b] text-sm">
                Dein Passwort wurde erfolgreich aktualisiert. Du wirst weitergeleitet…
              </p>
            </div>
          ) : hasRecoverySession === false ? (
            <div className="text-center py-2">
              <div className="w-14 h-14 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-[#111827] mb-2">Link ungültig</h3>
              <p className="text-[#6b6b6b] text-sm mb-4">
                Dieser Reset-Link ist abgelaufen oder ungültig. Bitte fordere einen neuen Link an.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm text-[#970000] font-semibold hover:underline"
              >
                Neuen Link anfordern
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-[#111827] mb-1">Neues Passwort</h2>
              <p className="text-sm text-[#6b6b6b] mb-5">
                Wähle ein neues, sicheres Passwort für dein Konto.
              </p>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-1.5">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className={inputClass + ' pr-12'}
                      placeholder="Sicheres Passwort"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9b9490] hover:text-[#111827]"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {password.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map(i => (
                          <div
                            key={i}
                            className={`flex-1 h-1 rounded-full ${
                              i <= passwordScore
                                ? passwordScore <= 1
                                  ? 'bg-red-500'
                                  : passwordScore === 2
                                    ? 'bg-amber-500'
                                    : passwordScore === 3
                                      ? 'bg-yellow-500'
                                      : 'bg-emerald-500'
                                : 'bg-[#eadfd6]'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        {[
                          { key: 'length', label: 'Min. 8 Zeichen' },
                          { key: 'uppercase', label: 'Großbuchstabe' },
                          { key: 'number', label: 'Zahl' },
                          { key: 'special', label: 'Sonderzeichen' },
                        ].map(check => {
                          const ok = passwordChecks[check.key as keyof typeof passwordChecks];
                          return (
                            <div
                              key={check.key}
                              className={`flex items-center gap-1.5 text-xs ${
                                ok ? 'text-emerald-700' : 'text-[#9b9490]'
                              }`}
                            >
                              {ok ? (
                                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <Circle className="h-3.5 w-3.5 shrink-0" />
                              )}
                              {check.label}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-1.5">
                    Passwort bestätigen
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className={inputClass}
                    placeholder="Passwort wiederholen"
                    required
                    autoComplete="new-password"
                  />
                  {confirm.length > 0 && confirm !== password && (
                    <p className="text-xs text-red-600 mt-1.5">
                      Passwörter stimmen nicht überein.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-[#970000] hover:bg-[#7a0000] text-white font-semibold rounded-2xl transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {submitting ? 'Wird gespeichert…' : 'Passwort ändern'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
